import React, { useState, useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { XCircle, Image } from "lucide-react";
import UploadErrors from "../../../../components/UploadErrors";
import { validateFile, describeAccept, formatFileSize } from "../../../../utils/file-validation";
import "./ImageUploadComponent.scss";

// Narrower than image/* on purpose: the server only stores these formats
// (config/multer.js IMAGE_EXTENSIONS), and a rejected image fails the whole
// test save, not just this field.
const DEFAULT_ACCEPT = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/webp': ['.webp'],
  'image/gif': ['.gif'],
  'image/bmp': ['.bmp'],
};
// questionRoutes.js caps each image at 10 MB.
const DEFAULT_MAX_SIZE = 10 * 1024 * 1024;

const acceptAttr = (accept) =>
  [...Object.keys(accept), ...Object.values(accept).flat()].join(',');

const ImageUploadComponent = ({
  imageUrl,
  onImageUpload,
  onImageRemove,
  label = "תמונה",
  placeholderText = "העלאת תמונה",
  disabled = false,
  accept = DEFAULT_ACCEPT,
  maxSize = DEFAULT_MAX_SIZE
}) => {
  const [isHovering, setIsHovering] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  // Reset file input when imageUrl changes
  useEffect(() => {
    if (!imageUrl && fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [imageUrl]);

  // The only gate before the file reaches the test payload. Without it an
  // oversized or unsupported image is only refused when the whole test is
  // saved, and the server's reply names no field, so the author has to guess
  // which of a dozen questions is at fault.
  const acceptFile = useCallback((file, event) => {
    const rejection = validateFile(file, { accept, maxSize });
    if (rejection) {
      setError(rejection.message);
      return;
    }
    // A valid pick answers whatever the previous complaint was.
    setError('');
    onImageUpload(event);
  }, [accept, maxSize, onImageUpload]);

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    acceptFile(file, e);
  };

  const handleRemoveImage = () => {
    setError('');
    onImageRemove();

    // Reset the file input to ensure we can select the same file again if needed
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUploadClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleKeyDown = (e) => {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleUploadClick();
    }
  };

  // The placeholder has always said "גרור ושחרר", but nothing listened for a
  // drop until now — the file just opened in a new tab and the user lost the form.
  const handleDragOver = (e) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragActive(false);
    if (disabled) return;

    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    // Hand the change handlers the same shape a picker change event has, so
    // callers stay unaware of where the file came from.
    acceptFile(file, { target: { files: e.dataTransfer.files } });
  };

  return (
    <div className="training-image-upload-section">
      <label className="training-image-upload-label">{label}</label>
      <div className="training-image-upload-container">
        {imageUrl ? (
          <div
            className="training-image-preview"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            <img
              src={imageUrl}
              alt={label}
              className="training-preview-image"
              style={{ opacity: isHovering ? 0.7 : 1 }}
            />
            {!disabled && (
              <button
                type="button"
                className="training-button training-button-danger training-button-small training-remove-image"
                onClick={handleRemoveImage}
                style={{ opacity: isHovering ? 1 : 0.8 }}
                aria-label="הסר תמונה"
              >
                <XCircle size={16} />
              </button>
            )}
          </div>
        ) : (
          <div
            className={`training-image-upload-button${disabled ? ' disabled' : ''}${isDragActive ? ' drag-active' : ''}`}
            onClick={handleUploadClick}
            onKeyDown={handleKeyDown}
            onDragOver={handleDragOver}
            onDragEnter={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-label={placeholderText}
          >
            <div className="training-upload-icon-container">
              <Image size={24} className="training-upload-icon" />
            </div>
            <div className="training-upload-text">
              <p className="training-upload-title">{placeholderText}</p>
              <p className="training-upload-subtitle">גרור ושחרר או לחץ לבחירת קובץ</p>
              <p className="training-upload-hint">
                {describeAccept(accept)} · עד {formatFileSize(maxSize)}
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept={acceptAttr(accept)}
              onChange={handleImageChange}
              disabled={disabled}
              style={{ display: 'none' }}
              aria-hidden="true"
              // Adding a key to force re-render when the file is cleared
              key={imageUrl ? 'has-image' : 'no-image'}
            />
          </div>
        )}
      </div>

      <UploadErrors
        messages={error ? [error] : []}
        onDismiss={() => setError('')}
      />
    </div>
  );
};

ImageUploadComponent.propTypes = {
  imageUrl: PropTypes.string,
  onImageUpload: PropTypes.func.isRequired,
  onImageRemove: PropTypes.func.isRequired,
  label: PropTypes.string,
  placeholderText: PropTypes.string,
  disabled: PropTypes.bool,
  accept: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
  maxSize: PropTypes.number
};

export default ImageUploadComponent;
